import { useCallback, useEffect } from 'react';
import { Location, NavigateFunction } from 'react-router-dom';
import { useSpring } from 'react-spring';
import useSchmittTrigger from '../../hooks/useSchmittTrigger';
import styles from './Sidebar.module.scss';
import SidebarContext from './SidebarContext';
import { animated } from 'react-spring';
import useThemeAnimation from '../../hooks/useThemeAnimation';
import { HeaderProps } from './Header/Header';
import { SectionProps } from './Section/Section';

interface SidebarProps {
    width?: number;
    minWidth?: number;
    threshold?: number;
    debounce?: number;
    collapsed?: boolean;
    children?: React.ReactElement<HeaderProps | SectionProps>[] | React.ReactElement<HeaderProps | SectionProps>;
    location: Location;
    navigator?: NavigateFunction;
    headerHeight?: number;
    header?: React.ReactElement;
}

function Sidebar({
    minWidth = 100,
    width = 200,
    threshold = 800,
    debounce = 25,
    collapsed = false,
    children,
    location,
    navigator = () => {},
    headerHeight=0,
    header,
}: SidebarProps) {
    const [autoCollapse, setAutoCollapse] = useSchmittTrigger({
        midpoint: threshold,
        hysteresis: debounce,
        initial: true,
        inverted: true,
    });

    const [shouldAnimate, animationConfig] = useThemeAnimation();

    const { animationProgress } = useSpring({
        to: {
            animationProgress: autoCollapse || collapsed ? 0 : 100,
        },
        config: animationConfig,
        immediate: !shouldAnimate,
    });

    const onResize = useCallback(() => {
        setAutoCollapse(window.innerWidth);
    }, [setAutoCollapse]);

    useEffect(() => {
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [onResize]);

    useEffect(() => {
        window.dispatchEvent(new Event('resize'));
    }, [onResize]);

    return (
        <SidebarContext.Provider
            value={{
                animationProgress: animationProgress,
                width: width,
                minWidth: minWidth,
                headerHeight: headerHeight,
                collapsed: autoCollapse || collapsed,
                location: location,
                navigator: navigator,
                provider: 'sidebar',
            }}
        >
            <animated.nav
                className={styles['sidebar']}
                style={{
                    width: animationProgress
                        .to({
                            range: [100, 0],
                            output: [width, minWidth],
                        })
                        .to(number => `${number}px`),
                }}
            >
                {header}
                <div style={{height: `calc(100% - ${headerHeight}px)`}}>
                    {children}
                </div>
            </animated.nav>
        </SidebarContext.Provider>
    );
}

export default Sidebar;
